import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { isEqual, kebabCase } from 'lodash-es';
import { Observable, of } from 'rxjs';
import { catchError, debounceTime, filter, map, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';

import { wsLink } from '../../features/shared/utils/workspace-link';
import { ExtractionRuleListActions } from './extraction-rule-list.actions';
import { AssetSettingFilters } from '../../features/settings/models/user-profile.model';
import { SemerisRoutes } from '../../features/shared/constants/routes';
import { Permission } from '../../features/shared/models/api/permissions.enum';
import { PermissionService } from '../../features/shared/services/permission.service';
import { createNotificationEffect, PushNotificationRecord } from '../../features/shared/utils/create-notification-effect';
import { toBarFilterValue, toLegacyFilterValue } from '../../features/shared/utils/filter-value-bridge';
import { reportEffectError } from '../../features/shared/utils/report-effect-error';
import { DatapointRule, DatapointRuleType } from '../../features/unified-datapoint/models/datapoint-rule.model';
import { FormulaRule } from '../../features/unified-datapoint/models/formula-rule.model';
import { LLMRule } from '../../features/unified-datapoint/models/llm-rule.model';
import { DatapointRuleService } from '../../features/unified-datapoint/services/datapoint-rule.service';
import { AppActions } from '../app/app.actions';
import { DatapointFieldListActions } from '../datapoint-field-list/datapoint-field-list.actions';
import { RootState } from '../root-state.model';
import { UserProfileActions } from '../user-profile/user-profile.actions';

@Injectable()
export class ExtractionRuleListEffects {
  private service = inject(DatapointRuleService);
  private actions$ = inject(Actions);
  private readonly store = inject<Store<RootState>>(Store);
  private readonly router = inject(Router);
  private readonly permissionService = inject(PermissionService);

  private readonly notifications: PushNotificationRecord = {
    [ExtractionRuleListActions.LoadListFail.type]: {
      message: 'Failed to load the datapoints',
      isSuccess: false,
    },
    [ExtractionRuleListActions.DeleteDatapointRuleFail.type]: {
      message: 'Failed to delete the datapoint',
      isSuccess: false,
    },
    [ExtractionRuleListActions.DeleteDatapointRuleSuccess.type]: {
      message: 'Datapoint deleted',
      isSuccess: true,
    },
    [ExtractionRuleListActions.CreateDatapointRuleSuccess.type]: {
      message: 'Datapoint created',
      isSuccess: true,
    },
    [ExtractionRuleListActions.CreateDatapointRuleFail.type]: {
      message: 'Failed to create the datapoint',
      isSuccess: false,
    },
  };

  loadListEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(ExtractionRuleListActions.LoadList),
      switchMap(() =>
        this.service.getAllDatapointRule().pipe(
          map((rules) => ExtractionRuleListActions.LoadListSuccess({ rules })),
          catchError((error) => of(ExtractionRuleListActions.LoadListFail({ error: error?.toString() }))),
        ),
      ),
    ),
  );

  refreshDatapointRuleEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(ExtractionRuleListActions.RefreshDatapointRule),
      switchMap((action) =>
        this.service.getById(action.id).pipe(map((rule) => ExtractionRuleListActions.RefreshDatapointRuleSuccess({ item: rule }))),
      ),
    ),
  );

  loadSingleRuleEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(ExtractionRuleListActions.LoadSingleRule),
      withLatestFrom(this.store),
      switchMap(([action, store]) =>
        this.service.getById(action.id).pipe(
          map((rule) => ExtractionRuleListActions.LoadSingleRuleSuccess({ rule })),
          catchError((error) => of(ExtractionRuleListActions.LoadSingleRuleFail({ error: error?.toString() }))),
        ),
      ),
    ),
  );

  createRuleEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(ExtractionRuleListActions.CreateDatapointRule),
      withLatestFrom(this.permissionService.hasEveryPermission([Permission.TEMPLATE_USER])),
      switchMap(([action, hasTemplatePermissions]) =>
        this.service
          .create(
            action.llmRule
              ? ({
                  ruleType: action.ruleType,
                  displayName: action.displayName,
                  rule: action.llmRule ? action.llmRule : {},
                  referenceNumber: action.referenceNumber,
                  assetShortNames: action.assetShortNames,
                  groundTruthDatapointField: action.groundTruthDatapointField,
                  shared: action.shared,
                  sharedForEdit: action.sharedForEdit,
                } as DatapointRule)
              : ({
                  ruleType: action.ruleType,
                  displayName: action.displayName,
                  rule: action.formulaId ? { formulaId: action.formulaId } : [],
                  referenceNumber: action.referenceNumber,
                  assetShortNames: action.assetShortNames,
                  shared: action.shared,
                  sharedForEdit: action.sharedForEdit,
                } as DatapointRule),
            true,
            hasTemplatePermissions,
          )
          .pipe(
            map((rule) =>
              ExtractionRuleListActions.CreateDatapointRuleSuccess({
                rule,
                routingQueryParams: action.routingQueryParams,
              }),
            ),
            reportEffectError(ExtractionRuleListActions.CreateDatapointRuleFail),
          ),
      ),
    ),
  );

  createRuleEffectNavigation$: Observable<any> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ExtractionRuleListActions.CreateDatapointRuleSuccess),
        filter((action) => !(action.rule?.rule as FormulaRule)?.formulaId),
        map((action) =>
          this.router.navigate(
            wsLink(SemerisRoutes.datapoint.root, SemerisRoutes.datapoint.rule.root, kebabCase(action.rule.ruleType), action.rule.id),
            { queryParams: action.routingQueryParams },
          ),
        ),
      ),
    { dispatch: false },
  );

  templateModalClosedCreateEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.TemplateModalClosed),
      filter((action) => action.modalId === 'edit-extraction-rule' && !!action.data),
      filter((action) => !action.data.id),
      map((action) => {
        const info = action.additionalInfo as { ruleType?: DatapointRuleType; formulaId?: string; referenceNumber?: string } | undefined;
        return ExtractionRuleListActions.CreateDatapointRule({
          displayName: action.data.displayName,
          ruleType: info?.ruleType,
          formulaId: info?.formulaId,
          referenceNumber: info?.referenceNumber ?? action.data.referenceNumber,
          assetShortNames: action.data.assetShortNames,
          shared: action.data.shared,
          sharedForEdit: action.data.sharedForEdit,
        });
      }),
    ),
  );

  deleteRuleEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(ExtractionRuleListActions.DeleteDatapointRule),
      withLatestFrom(this.store),
      switchMap(([action, store]) => {
        const rule = store.extractionRuleList.rules?.find((r) => r.id === action.id);
        const llmRule = rule?.rule as LLMRule;
        const isLLM = rule?.ruleType === DatapointRuleType.LLM;
        const groundTruthDatapointFieldId = rule?.groundTruthDatapointField;
        const isSystemGeneratedGT = groundTruthDatapointFieldId === llmRule?.systemGroundTruth;

        return this.service.delete(action.id).pipe(
          mergeMap(() => {
            const actions: Action[] = [
              ExtractionRuleListActions.DeleteDatapointRuleSuccess({
                id: action.id,
                navigate: action.navigate,
              }),
            ];

            // If the rule is an LLM with a system-generated GT field, remove it from the list
            // (BE already deleted it as a side effect)
            if (isLLM && groundTruthDatapointFieldId && isSystemGeneratedGT) {
              actions.push(DatapointFieldListActions.RemoveDatapointFieldFromList({ id: groundTruthDatapointFieldId }));
            }

            return actions;
          }),
          reportEffectError(ExtractionRuleListActions.DeleteDatapointRuleFail),
        );
      }),
    ),
  );

  deleteRuleEffectNavigation$: Observable<any> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ExtractionRuleListActions.DeleteDatapointRuleSuccess),
        filter((action) => !!action.navigate),
        map((action) => this.router.navigate(wsLink(SemerisRoutes.datapoint.root))),
      ),
    { dispatch: false },
  );

  cloneModelEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(ExtractionRuleListActions.CloneModel),
      withLatestFrom(this.store),
      switchMap(([action, store]) =>
        this.service.clone(action.id).pipe(
          map((datapointRule) => ExtractionRuleListActions.CloneModelSuccess({ datapointRule })),
          reportEffectError(ExtractionRuleListActions.CloneModelFail),
        ),
      ),
    ),
  );

  cloneChecklistSuccessNavigateEffect$: Observable<any> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ExtractionRuleListActions.CloneModelSuccess),
        map((action) => this.router.navigate(action.datapointRule.routerLink)),
      ),
    { dispatch: false },
  );

  notificationEffect$: Observable<Action> = createNotificationEffect(this.actions$, this.notifications);

  syncFiltersEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ExtractionRuleListActions.Filter),
      debounceTime(500),
      withLatestFrom(this.store),
      map(([action, store]) => {
        const filters: AssetSettingFilters = {
          datapoint: toLegacyFilterValue(store.extractionRuleList.searchFilter),
        };

        return UserProfileActions.UpdateAssetSetting({
          setting: {
            filters,
          },
        });
      }),
    ),
  );

  refreshFiltersEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.RefreshAssetSettingSuccess),
      map((action) => toBarFilterValue((action.assetSetting?.filters as AssetSettingFilters)?.datapoint)),
      withLatestFrom(this.store),
      filter(([filter, store]) => !!filter && !isEqual(filter, store.extractionRuleList.searchFilter)),
      map(([filter, store]) => ExtractionRuleListActions.Filter({ filter })),
    ),
  );
}
